import React, { useEffect, useState, useCallback, useRef } from "react";
import { Calendar, Users, MapPin, Building, Filter } from 'lucide-react';
import axios from "axios";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import { FaSearch } from "react-icons/fa";

const DCRMonthlyPOBInSlabScheme = () => {
    const [states, setStates] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedState, setSelectedState] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("All");
    const [selectedEmployee, setSelectedEmployee] = useState("All");
    const [productiveGT, setProductiveGT] = useState();
    const today = new Date().toISOString().split('T')[0];
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    const startDateStr = startOfMonth.toISOString().split('T')[0];

    const [fromDate, setFromDate] = useState(startDateStr);
    const [toDate, setToDate] = useState(today);
    const [tableData, setTableData] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [errors, setErrors] = useState({
        state: false,
        employee: false
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const inputRef = useRef(null);
    const handleExpandSearch = () => {
        setIsSearchExpanded(true);
        setTimeout(() => inputRef.current?.focus(), 100);
    };
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        handleGoClick();
    };
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };
    const fetchEmployees = useCallback(async (stateId, divisionName) => {
        if (!stateId) return;
        try {
            let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
            if (divisionName && divisionName !== "All") {
                url += `&division=${encodeURIComponent(divisionName)}`;
            }
            const res = await axios.get(url, {
                headers: getAuthHeaders()
            });
            setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
            setSelectedEmployee("All");
        } catch (err) {
            console.error("Error fetching employees:", err);
            setEmployees([]);
        }
    }, []);

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/states`, {
                    headers: getAuthHeaders()
                });
                setStates(response.data);
            } catch (err) {
                console.error("Error fetching states:", err);
            }
        };
        fetchStates();
    }, []);

    const handleStateChange = async (stateId) => {
        setSelectedState(stateId);
        setSelectedDivision("All");
        setSelectedEmployee("All");
        setCurrentPage(1);

        try {
            const divisionRes = await axios.get(
                `${BASE_URL}/api/divisions?state_id=${stateId}`,
                { headers: getAuthHeaders() }
            );
            setDivisions(divisionRes.data);
        } catch (err) {
            console.error("Error loading divisions", err);
        }

        fetchEmployees(stateId, "All");
    };

    const handleDivisionChange = async (divisionId) => {
        setSelectedDivision(divisionId);
        setSelectedEmployee("All");
        setCurrentPage(1);
        
        try {
            let url = `${BASE_URL}/api/employees?state_id=${selectedState}`;
            if (divisionId && divisionId !== "All") {
                const division = divisions.find(d => d.id == divisionId);
                if (division) {
                    url += `&division=${encodeURIComponent(division.name)}`;
                }
            }
            
            const res = await axios.get(url, { headers: getAuthHeaders() });
            setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
        } catch (err) {
            console.error("Error fetching employees:", err);
            setEmployees([]);
        }
    };

    const handleGoClick = useCallback(async () => {
        const newErrors = {
            state: !selectedState,
            employee: false
        };
        
        setErrors(newErrors);
        
        if (newErrors.state) {
            return;
        }
        
        setShowTable(true);
        try {
            let url = `${BASE_URL}/api/reports/dcrmonthlypobinslabscheme?state_id=${selectedState}`;
            
            // Add division filter if selected (not "All")
            if (selectedDivision && selectedDivision !== "All") {
                const division = divisions.find(d => d.id == selectedDivision);
                if (division) {
                    url += `&division=${encodeURIComponent(division.name)}`;
                }
            }
            
            // Add employee filter if selected (not "All")
            if (selectedEmployee && selectedEmployee !== "All") {
                url += `&employee_id=${selectedEmployee}`;
            }
            
            // Add other filters
            if (fromDate) url += `&from_date=${fromDate}`;
            if (toDate) url += `&to_date=${toDate}`;
            if (productiveGT) url += `&productive_gt=${productiveGT}`;
            
            // Add search query if exists
            if (searchQuery) {
                url += `&search=${encodeURIComponent(searchQuery)}`;
            }

            // Add pagination parameters
            const skip = (currentPage - 1) * itemsPerPage;
            url += `&page=${currentPage}&limit=${itemsPerPage}`;

            const response = await axios.get(url, { headers: getAuthHeaders() });
            console.log("API Response", response);
            
            // Handle both array and object responses
            if (response.data) {
                if (Array.isArray(response.data)) {
                    // Handle case where API returns array directly
                    setReportData(response.data);
                    setTotalRecords(response.data.length);
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    // Handle paginated response
                    setReportData(response.data.data);
                    setTotalRecords(response.data.total_records || response.data.data.length);
                } else {
                    // Handle other response formats
                    setReportData([]);
                    setTotalRecords(0);
                }
            } else {
                setReportData([]);
                setTotalRecords(0);
            }

        } catch (error) {
            console.error("Error fetching report data:", error);
            setReportData([]);
            setTotalRecords(0);
        }
    }, [
        selectedState, 
        selectedDivision, 
        selectedEmployee, 
        fromDate, 
        toDate, 
        productiveGT, 
        searchQuery,
        currentPage,
        itemsPerPage,
        divisions
    ]);

    // Fetch data when pagination or search changes
    useEffect(() => {
        if (showTable) {
            handleGoClick();
        }
    }, [currentPage, itemsPerPage, searchQuery, handleGoClick]);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${date.getDate().toString().padStart(2, '0')} ${months[date.getMonth()]} ${date.getFullYear()}`;
        } catch (e) {
            return "N/A";
        }
    };

    return (
        <div className="DCRMonthlyPOBInSlabScheme-main-container">
            <div className="min-h-screen bg-gray-50">
                <div className="p-4">
                   <div className="bg-white border border-gray-800 rounded-lg mt-5 p-6 w-full shadow-md">
    <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
        <span className="text-blue-700">Visit / </span>
        <span className="text-blue-500">Support</span>
    </h1>

    {/* Row 1: State, Division, Employee */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-1 gap-y-2 mb-1 place-items-center">
        {/* State */}
        <div className="flex flex-col items-center w-full">
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                State
            </label>
            <select 
                className={`w-60 mx-auto p-3 border border-gray-300 rounded-md text-sm focus:ring-0 focus:border-blue-500 ${errors.state ? 'border-red-500' : ''}`}
                onChange={(e) => {
                    handleStateChange(e.target.value);
                    setErrors({ ...errors, state: false });
                }}
                value={selectedState}
                required
            >
                <option value="">Select State</option>
                {states.map((state) => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                ))}
            </select>
        </div>

        {/* Division */}
        <div className="flex flex-col items-center w-full">
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Building className="w-4 h-4 mr-1" />
                Division
            </label>
            <select 
                className="w-60 mx-auto p-3 border border-gray-300 rounded-md text-sm focus:ring-0 focus:border-blue-500"
                onChange={(e) => handleDivisionChange(e.target.value)}
                value={selectedDivision}
            >
                <option value="All">All</option>
                {divisions.map((div) => (
                    <option key={div.id} value={div.id}>{div.name}</option>
                ))}
            </select>
        </div>

        {/* Employee */}
        <div className="flex flex-col items-center w-full">
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Users className="w-4 h-4 mr-1" />
                Employee
            </label>
            <select 
                className={`w-80 mx-auto p-3 border border-gray-300 rounded-md text-sm focus:ring-0 focus:border-blue-500 ${errors.employee ? 'border-red-500' : ''}`}
                onChange={(e) => {
                    setSelectedEmployee(e.target.value);
                    setErrors({ ...errors, employee: false });
                }}
                value={selectedEmployee}
            >
                <option value="All">All</option>
                {employees.map((emp) => (
                    emp.id !== "All" && (
                        <option key={emp.id} value={emp.id}>
                            {`${emp.first_name} ${emp.last_name} (${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name})`}
                        </option>
                    )
                ))}
            </select>
        </div>
    </div>

    {/* Row 2: Start Date, End Date, Productive Greater Than */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-1 gap-y-2 mb-3 place-items-center">
        {/* Start Date */}
        <div className="flex flex-col items-center w-full">
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Start Date
            </label>
            <input 
                type="date" 
                value={fromDate} 
                onChange={(e) => setFromDate(e.target.value)} 
                className="w-60 mx-auto p-3 border border-gray-300 rounded-md text-sm focus:ring-0 focus:border-blue-500"
            />
        </div>

        {/* End Date */}
        <div className="flex flex-col items-center w-full">
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                End Date
            </label>
            <input 
                type="date" 
                value={toDate} 
                onChange={(e) => setToDate(e.target.value)} 
                className="w-60 mx-auto p-3 border border-gray-300 rounded-md text-sm focus:ring-0 focus:border-blue-500"
            />
        </div>

        {/* Productive Greater Than */}
        <div className="flex flex-col items-center w-full">
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Filter className="w-4 h-4 mr-1" />
                Productive Greater Than
            </label>
            <input 
                type="number" 
                value={productiveGT} 
                onChange={(e) => setProductiveGT(e.target.value)} 
                placeholder="Enter value..."
                className="w-60 mx-auto p-3 border border-gray-300 rounded-md text-sm focus:ring-0 focus:border-blue-500"
            />
        </div>
    </div>

    {/* Row 3: Go Button */}
    <div className="flex justify-center">
        <button 
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-md text-sm font-medium transition-colors duration-200 shadow-md hover:shadow-lg"
            onClick={handleGoClick}
        >
            GO
        </button>
    </div>
</div>

                    {/* Table Section */}
                    {showTable && (
                        <>
                            <div className="bg-white rounded-lg shadow-md mt-6 overflow-hidden">
                                <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
                                    <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800">
                                        <span className="text-blue-700"> All </span>
                                        <span className="text-blue-500">Employees</span>
                                    </h1>
                                    
                                    {/* Search Bar */}
                                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            placeholder="Search employees..."
                                            className={`transition-all duration-300 ease-in-out border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isSearchExpanded || searchQuery
                                                    ? "w-64 opacity-100"
                                                    : "w-0 opacity-0 px-0 py-0"
                                            }`}
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                if (e.target.value === "") {
                                                    setCurrentPage(1);
                                                }
                                            }}
                                            onBlur={() => !searchQuery && setIsSearchExpanded(false)}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleExpandSearch}
                                            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                        >
                                            <FaSearch className="w-5 h-5" />
                                        </button>
                                    </form>
                                </div>
                                
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">S.No</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Emp ID</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Employee</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">State</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">HQ</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Designation</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Division</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">DOJ</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Manager</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Working Days</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total POB</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {reportData.length > 0 ? (
                                                reportData.map((item, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                            {(currentPage - 1) * itemsPerPage + index + 1}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.emp_id || "N/A"}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.employee || ""}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.state || ""}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.hq || ""}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.designation || ""}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.division || ""}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDate(item.doj)}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.manager || ""}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                            {item.working_days ? formatDate(item.working_days) : 'No working days'}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {item.total_pob ? item.total_pob.toFixed(2) : "0.00"}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="11" className="px-4 py-3 text-center text-sm text-gray-500">
                                                        No records found. Please try different filters.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {reportData.length > 0 && (
                                    <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300">
                                        <div className="flex items-center mb-2 sm:mb-0">
                                            <label htmlFor="itemsPerPage" className="mr-2 font-medium text-sm text-gray-700">
                                                View
                                            </label>
                                            <select
                                                id="itemsPerPage"
                                                className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none"
                                                value={itemsPerPage}
                                                onChange={handleItemsPerPageChange}
                                            >
                                                <option value="10">10</option>
                                                <option value="20">20</option>
                                                <option value="30">30</option>
                                                <option value="50">50</option>
                                            </select>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <span className="text-sm text-gray-600 font-medium">
                                                {(currentPage - 1) * itemsPerPage + 1}-
                                                {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords}
                                            </span>
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                ←
                                            </button>
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage * itemsPerPage >= totalRecords}
                                                className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                →
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-center space-x-4 mt-6 mb-8">
                                <button className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-md hover:shadow-lg">
                                    Print
                                </button>
                                <button className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-md hover:shadow-lg">
                                    Export to Excel
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DCRMonthlyPOBInSlabScheme;